import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { FormField } from '@/shared/components/ui/FormField';
import { Icon } from '@/shared/icons';
import { useCredits } from '../hooks';
import type { CancellationReason } from '../types';
import { formatDateSafe } from '../utils/formatDateSafe';

interface SubscriptionInfo {
  currentCycleEnd: string | null | undefined;
}

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: SubscriptionInfo;
}

const REASON_OPTIONS = [
  { value: '', label: 'Selecione um motivo...', disabled: true },
  { value: 'too_expensive', label: 'Preço muito alto' },
  { value: 'unused_features', label: 'Não utilizo todas as funcionalidades' },
  { value: 'found_alternative', label: 'Encontrei uma alternativa melhor' },
  { value: 'technical_issues', label: 'Problemas técnicos frequentes' },
  { value: 'poor_support', label: 'Atendimento insatisfatório' },
  { value: 'business_closing', label: 'Empresa fechando/mudando de ramo' },
  { value: 'other', label: 'Outro' },
];

function formatCycleEndDate(date: Date | string | null | undefined): string {
  return formatDateSafe(date, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Modal de cancelamento de assinatura — Modal shared (F72).
 */
export const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  subscription,
}) => {
  const { cancelSubscriptionAsync, isCancellingSubscription } = useCredits();

  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [confirmStep, setConfirmStep] = useState(false);

  // Reset confirm step after 5 seconds timeout
  useEffect(() => {
    if (!confirmStep) return;
    const timer = setTimeout(() => setConfirmStep(false), 5000);
    return () => clearTimeout(timer);
  }, [confirmStep]);

  // Reset confirm step when reason or details change
  useEffect(() => {
    setConfirmStep(false);
  }, [reason, details]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setDetails('');
      setConfirmStep(false);
    }
  }, [isOpen]);

  const canSubmit = useCallback((): boolean => {
    if (!reason) return false;
    if (reason === 'other' && !details.trim()) return false;
    return true;
  }, [reason, details]);

  const handleCancelClick = async () => {
    if (!confirmStep) {
      setConfirmStep(true);
      return;
    }

    try {
      await cancelSubscriptionAsync({
        reason: reason as CancellationReason,
        details: details.trim() || undefined,
      });
      onClose();
    } catch {
      setConfirmStep(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="lg">
      <div data-testid="cancel-subscription-modal">
        <ModalHeader title="Cancelar Assinatura" onClose={onClose}>
          <div
            className="flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0"
            style={{ backgroundColor: '#fee2e2' }}
          >
            <Icon name="alert-triangle" className="h-5 w-5" style={{ color: '#dc2626' }} />
          </div>
        </ModalHeader>

        <ModalContent className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto" padding={false}>
          {/* Red info box */}
          <div
            className="flex items-start gap-3 p-4 rounded-lg"
            style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
          >
            <Icon name="info" className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#dc2626' }} />
            <p className="text-sm" style={{ color: '#991b1b' }}>
              Você manterá acesso até{' '}
              <strong>{formatCycleEndDate(subscription.currentCycleEnd)}</strong>.
              Após essa data, os recursos serão desativados.
            </p>
          </div>

          {/* Cancellation reason */}
          <FormField label="Motivo do cancelamento" required>
            <Select
              value={reason}
              onChange={(val) => setReason(val)}
              options={REASON_OPTIONS}
              fullWidth
              data-testid="cancel-subscription-reason"
            />
          </FormField>

          {/* Details textarea */}
          <FormField
            label="Detalhes (opcional)"
            required={reason === 'other'}
          >
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              placeholder="Conte-nos mais sobre sua decisão..."
              fullWidth
              data-testid="cancel-subscription-details"
            />
          </FormField>
        </ModalContent>

        <ModalFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isCancellingSubscription}
            data-testid="cancel-subscription-back"
          >
            Voltar
          </Button>
          <Button
            variant="error"
            onClick={handleCancelClick}
            disabled={!canSubmit() || isCancellingSubscription}
            data-testid="cancel-subscription-submit"
          >
            {isCancellingSubscription
              ? 'Cancelando...'
              : confirmStep
                ? 'Confirmar Cancelamento'
                : 'Cancelar Assinatura'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

CancelSubscriptionModal.displayName = 'CancelSubscriptionModal';
